/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-redeclare */
import BreadCrumb from "Common/BreadCrumb";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Flatpickr from "react-flatpickr";
import { GripVertical, Plus, Search } from "lucide-react";
import { Link } from "react-router-dom";
import Modal from "Common/Components/Modal";
import DeleteModal from "Common/DeleteModal";
import Loader from "Common/Components/Loader";
import { useFormik } from "formik";
import { APIClient } from "helpers/api_helper";
import { toast, ToastContainer } from "react-toastify";
import { runLotteryDrawNow, updateGameSlot } from "services/apiGameSlots";
import * as Yup from "yup";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCreateSlot, useDeleteSlot, useGameSlots } from "./useGameSlot";
import { useQuestionSets } from "pages/Trivia/useTrivia";
import WinningNumbersContent from "../winners/WinningNumbersContent";
import { useGames } from "pages/games/useGames";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import TableTwoContainer from "Common/TableTwoContainer";
import {
  getRecurringSchedules,
  createRecurringSchedule,
  updateRecurringSchedule,
  deleteRecurringSchedule,
} from "services/apiRecurringSchedules";
import { Repeat, Trash2, Pencil } from "lucide-react";

interface GameSlots {
  id: unknown;
  gameSlotId: string;
}

const GameSlots = () => {
  const size = 10;
  const {
    gameSlots,
    isFetching,
    hasNextPage,
    isLoading,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
    totalPages,
    totalElements,
    currentPage,
  } = useGameSlots(size);

  const { games: gameList } = useGames();
  const { createSlot, isCreating } = useCreateSlot();
  const { deleteSlot } = useDeleteSlot();
  const { questionSets } = useQuestionSets();
  const [data, setData] = useState<any>([]);
  const [eventData, setEventData] = useState<any>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const [showActionBar, setShowActionBar] = useState<string | null>(null);
  const [show, setShow] = useState<boolean>(false);
  const [radioOption, setRadioOption] = useState<"percentage" | "amount">(
    "percentage"
  );
  const [
    selectedGameSlotForWinningNumbers,
    setSelectedGameSlotForWinningNumbers,
  ] = useState<GameSlots | null>(null);
  const [winningNumbersModal, setWinningNumbersModal] = useState(false);

  const queryClient = useQueryClient();
  const { data: recurringSchedules = [], isLoading: schedulesLoading } = useQuery({
    queryKey: ["recurringSchedules"],
    queryFn: getRecurringSchedules,
  });
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleEdit, setScheduleEdit] = useState<any>(null);
  const scheduleCreateMutation = useMutation({
    mutationFn: createRecurringSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurringSchedules"] });
      toast.success("Recurring schedule created. Slots will be created automatically.");
      setShowScheduleModal(false);
      setScheduleForm({});
    },
    onError: (e: any) => toast.error(e?.message || "Failed to create schedule"),
  });
  const scheduleUpdateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) => updateRecurringSchedule(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurringSchedules"] });
      toast.success("Schedule updated.");
      setShowScheduleModal(false);
      setScheduleEdit(null);
      setScheduleForm({});
    },
    onError: (e: any) => toast.error(e?.message || "Failed to update schedule"),
  });
  const scheduleDeleteMutation = useMutation({
    mutationFn: deleteRecurringSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurringSchedules"] });
      toast.success("Schedule deleted.");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to delete schedule"),
  });
  const [scheduleForm, setScheduleForm] = useState<any>({
    gameId: "",
    frequency: "WEEKLY",
    dayOfWeek: 1,
    drawTime: "14:00",
    totalNumberOfTickets: 50,
    amountToBeWon: "",
    winnerPercentage: 15,
    numberOfWinners: "",
    winnerAmount: "",
    winnerAmounts: [],
    active: true,
  });
  const openScheduleModal = (edit?: any) => {
    if (edit) {
      setScheduleEdit(edit);
      const numWin = edit.numberOfWinners ? Number(edit.numberOfWinners) : 0;
      const amounts = Array.isArray(edit.winnerAmounts) && edit.winnerAmounts.length === numWin
        ? edit.winnerAmounts
        : Array.from({ length: numWin }, () => "");
      setScheduleForm({
        gameId: edit.gameId,
        frequency: edit.frequency || "WEEKLY",
        dayOfWeek: edit.dayOfWeek ?? 1,
        drawTime: edit.drawTime ? String(edit.drawTime).substring(0, 5) : "14:00",
        totalNumberOfTickets: edit.totalNumberOfTickets ?? 50,
        amountToBeWon: edit.amountToBeWon ?? "",
        winnerPercentage: edit.winnerPercentage ?? 15,
        numberOfWinners: edit.numberOfWinners ?? "",
        winnerAmount: edit.winnerAmount ?? "",
        winnerAmounts: amounts,
        active: edit.active !== false,
      });
    } else {
      setScheduleEdit(null);
      setScheduleForm({
        gameId: "",
        frequency: "WEEKLY",
        dayOfWeek: 1,
        drawTime: "14:00",
        totalNumberOfTickets: 50,
        amountToBeWon: "",
        winnerPercentage: 15,
        numberOfWinners: "",
        winnerAmount: "",
        winnerAmounts: [],
        active: true,
      });
    }
    setShowScheduleModal(true);
  };
  const submitSchedule = () => {
    const payload: any = {
      gameId: Number(scheduleForm.gameId),
      frequency: scheduleForm.frequency,
      drawTime: scheduleForm.drawTime,
      totalNumberOfTickets: Number(scheduleForm.totalNumberOfTickets),
      amountToBeWon: Number(scheduleForm.amountToBeWon),
      winnerPercentage: scheduleForm.winnerPercentage != null ? Number(scheduleForm.winnerPercentage) : null,
      numberOfWinners: scheduleForm.numberOfWinners ? Number(scheduleForm.numberOfWinners) : null,
      winnerAmount: scheduleForm.winnerAmount ? Number(scheduleForm.winnerAmount) : null,
      active: scheduleForm.active !== false,
      autoGenerateQuestions: true,
    };
    if (payload.frequency === "WEEKLY") {
      payload.dayOfWeek = Number(scheduleForm.dayOfWeek);
    }
    const n = Number(scheduleForm.numberOfWinners) || 0;
    const winnerAmounts = (scheduleForm.winnerAmounts || []).slice(0, n).map((v: any) => (v === "" || v == null ? 0 : Number(v)));
    if (n > 0 && winnerAmounts.length === n && winnerAmounts.some((a: number) => a > 0)) {
      payload.winnerAmounts = winnerAmounts;
      payload.amountToBeWon = winnerAmounts.reduce((a: number, b: number) => a + b, 0);
    }
    if (!payload.gameId || !payload.amountToBeWon) {
      toast.error("Please select a game and enter amount to be won (or winner amounts).");
      return;
    }
    if (scheduleEdit) {
      scheduleUpdateMutation.mutate({ id: scheduleEdit.id, payload });
    } else {
      scheduleCreateMutation.mutate(payload);
    }
  };

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  const handleDelete = async () => {
    if (selectedSlotId) {
      try {
        await deleteSlot(selectedSlotId);
        setDeleteModal(false);
        setSelectedSlotId(null);
      } catch (error) {
        console.error("Error deleting slot:", error);
      }
    }
  };

  // validation
  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      gameId: (eventData && eventData.gameId) || "",
      totalNumberOfTickets: (eventData && eventData.totalNumberOfTickets) || "",
      drawDate: eventData?.drawDate
        ? convertToISOString(eventData.drawDate)
        : "",
      numberOfWinners: (eventData && eventData.numberOfWinners) || "",
      winPercentage: (eventData && eventData.winnerPercentage) || "",
      amountToBeWon: (eventData && eventData.amountToBeWon) || "",
      winnerAmount: (eventData && eventData.winnerAmount) || "",
      winnerAmounts: (eventData && eventData.winnerAmounts) || [],
      questionSetId: (eventData && eventData.questionSetId) || "",
      questionSelectionType: "auto",
    },
    validationSchema: Yup.object({
      gameId: Yup.number().required("Please select a Game"),
      totalNumberOfTickets: Yup.number().required("Please Add Quantity"),
      winPercentage: Yup.number().max(50, "Max is 50%"),
      winnerAmount: Yup.number(),
      winnerAmounts: Yup.array().of(Yup.number().min(0)),
      drawDate: Yup.string()
        .required("Please Enter Start Date")
        .test("not-past-date", "Start date cannot be in the past", (value) =>
          value ? moment.parseZone(value).isSameOrAfter(moment(), "minute") : false
        ),
      numberOfWinners: Yup.number(),
      questionSetId: Yup.string().when("questionSelectionType", {
        is: "select",
        then: (schema) => schema.required("Please select a Question Set"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),

    onSubmit: async (values) => {
      try {
        if (isEdit) {
          const updatePayload: any = {
            ...values,
            winnerPercentage: values.winPercentage,
          };
          if (values.numberOfWinners && values.winnerAmounts && Array.isArray(values.winnerAmounts)) {
            const n = Number(values.numberOfWinners);
            const amounts = values.winnerAmounts.slice(0, n).map((v: any) => (v === "" || v == null ? 0 : Number(v)));
            if (n > 0 && amounts.length === n && amounts.some((a: number) => a > 0)) {
              updatePayload.winnerAmounts = amounts;
              updatePayload.amountToBeWon = amounts.reduce((a: number, b: number) => a + b, 0);
            }
          }
          await updateGameSlot(eventData.id, updatePayload);
        } else {
          const payload: {
            gameId: any;
            totalNumberOfTickets: any;
            drawDate: any;
            numberOfWinners: any;
            winnerAmount?: any;
            winnerAmounts?: number[];
            winnerPercentage: any;
            amountToBeWon: any;
            autoGenerateQuestions: boolean;
            questionSetId?: string;
          } = {
            gameId: values.gameId,
            totalNumberOfTickets: values.totalNumberOfTickets,
            drawDate: values.drawDate,
            numberOfWinners: values.numberOfWinners,
            winnerPercentage: values.winPercentage,
            amountToBeWon: values.amountToBeWon,
            autoGenerateQuestions: values.questionSelectionType !== "select",
          };

          if (values.numberOfWinners && values.winnerAmounts && Array.isArray(values.winnerAmounts)) {
            const n = Number(values.numberOfWinners);
            const amounts = values.winnerAmounts.slice(0, n).map((v: any) => (v === "" || v == null ? 0 : Number(v)));
            if (n > 0 && amounts.length === n && amounts.some((a: number) => a > 0)) {
              payload.winnerAmounts = amounts;
              payload.amountToBeWon = amounts.reduce((a: number, b: number) => a + b, 0);
            } else if (radioOption === "amount") {
              payload.winnerAmount = values.winnerAmount ? Number(values.winnerAmount) : null;
            }
          } else if (radioOption === "amount") {
            payload.winnerAmount = values.winnerAmount ? Number(values.winnerAmount) : null;
          }

          if (values.questionSelectionType === "select") {
            payload.questionSetId = values.questionSetId;
          }

          createSlot(payload);
        }
      } catch (error) {
        toast.error(
          error.message || `Failed to ${isEdit ? "update" : "create"} game slot`
        );
      } finally {
        toggle(); // Close the modal
      }
    },
  });

  const gamesArray: any[] = Array.isArray(gameList)
    ? gameList
    : ((gameList as { data?: any[] } | null)?.data ?? []);
  const selectedGame = gamesArray.find(
    (game: any) => String(game?.id) === String(validation.values.gameId ?? "")
  );
  const isRadioGame = selectedGame?.type === "RADIO";
  const isTriviaGame = selectedGame?.type === "TRIVIA";
  //
  const toggle = useCallback(() => {
    if (show) {
      setShow(false);
      setEventData("");
      setIsEdit(false);
    } else {
      setShow(true);
      setEventData("");
      validation.resetForm();
    }
  }, [show, validation]);

  function formatDateString(dateArray: number[]): string {
    const [year, month, day, hour, minute] = dateArray;
    const date = new Date(year, month - 1, day, hour, minute);

    const formattedDate = date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return formattedDate.replace(",", "").toLowerCase();
  }

  function convertToISOString(dateArray: number[]): string {
    if (!Array.isArray(dateArray) || dateArray.length < 5) {
      console.error("Invalid date array format:", dateArray);
      return "";
    }

    const [year, month, day, hour, minute, second] = [
      dateArray[0],
      dateArray[1],
      dateArray[2],
      dateArray[3] ?? 0,
      dateArray[4] ?? 0,
      dateArray[5] ?? 0,
    ];

    // Backend LocalDateTime arrays are effectively "local clock" components.
    // Build a local Date (not UTC) to avoid shifting the intended time backwards.
    const date = new Date(year, (month ?? 1) - 1, day ?? 1, hour, minute, second);

    if (isNaN(date.getTime())) {
      console.error("Invalid date value:", dateArray);
      return "";
    }

    return date.toISOString(); // Returns format "YYYY-MM-DDTHH:mm:ss.SSSZ"
  }
  const handleActionClick = (id: string) => {
    setShowActionBar((prev) => (prev === id ? null : id)); // Toggle visibility
  };

  useEffect(() => {
    if (radioOption === "percentage") {
      validation.setFieldValue("winnerAmount", "");
      validation.setFieldValue("winnerAmounts", []);
    } else if (radioOption === "amount") {
      validation.setFieldValue("winPercentage", "");
    }
  }, [radioOption]);

  // When "Winner Amount" and numberOfWinners change, sync winnerAmounts array length
  const numWinners = Number(validation.values.numberOfWinners) || 0;
  useEffect(() => {
    if (radioOption === "amount" && isRadioGame && numWinners > 0) {
      const current = (validation.values.winnerAmounts as any[]) || [];
      if (current.length !== numWinners) {
        const next = Array.from({ length: numWinners }, (_, i) => (current[i] !== undefined && current[i] !== "" ? current[i] : ""));
        validation.setFieldValue("winnerAmounts", next);
      }
    }
  }, [radioOption, isRadioGame, numWinners]);

  // columns
  const Status = ({ item }: any) => {
    switch (item) {
      case "CLOSED":
        return (
          <span className="delivery_status px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-red-100 border-red-200 text-red-500 dark:bg-red-500/20 dark:border-red-500/20">
            {item}
          </span>
        );
      default:
        return (
          <span className="delivery_status px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-green-100 border-green-200 text-green-500 dark:bg-green-500/20 dark:border-green-500/20">
            {item}
          </span>
        );
    }
  };
  const handleUpdateDataClick = (data) => {
    setEventData({ ...data });
    setIsEdit(true);
    setShow(true);
  };

  const showWinningNumbers = (gameSlotId) => {
    setSelectedGameSlotForWinningNumbers(gameSlotId);
    setWinningNumbersModal(true);
  };
  // columns
  const columns = useMemo(
    () => [
      {
        header: "Slot ID",
        accessorKey: "referenceId",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <Link
            to="#!"
            className="transition-all duration-150 ease-linear text-custom-500 hover:text-custom-600 w-32"
          >
            {cell.getValue()}
          </Link>
        ),
        meta: {
          className: "w-32",
        },
      },
      {
        header: "Slot Title",
        accessorKey: "slotTitle",
        enableColumnFilter: true,
        cell: (cell: any) => (
          <Link to="#!" className="flex items-center gap-3">
            <div className="size-10 rounded-full shrink-0 bg-slate-100">
              <img
                src={cell.row.original.image}
                alt=""
                className="h-10 rounded-full"
              />
            </div>
            <h6 className="grow">{cell.getValue()}</h6>
          </Link>
        ),
      },
      {
        header: "Slot Type",
        accessorKey: "type",
        enableColumnFilter: true,
      },
      {
        header: "Tickets Quantity",
        accessorKey: "totalNumberOfTickets",
        enableColumnFilter: false,
      },
      {
        header: "Sold | Remaining",
        accessorKey: "soldTickets",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <Link to="#!" className="flex items-center gap-3">
            <div>
              <h6 className="grow">{cell.row.original.soldTickets}</h6>
            </div>
            &nbsp;|&nbsp;
            <h6 className="grow">{cell.row.original?.remainingTickets}</h6>
          </Link>
        ),
      },
      {
        header: "Draw Date",
        accessorKey: "drawDate",
        enableColumnFilter: false,
        cell: (cell: any) => <p>{formatDateString(cell.getValue())}</p>,
      },

      {
        header: "Status",
        accessorKey: "state",
        enableColumnFilter: true,
        cell: (cell: any) => <Status item={cell.getValue()} />,
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cell) => {
          // Extract the row data
          const rowData = cell.row.original;
          const uniqueId = rowData.id;
          const isLottery = rowData.type === "LOTTERY"; // Check if it's a lottery game

          return (
            <div className="relative">
              {/* Action button should always be visible */}
              <button
                className="size-8 rounded-md bg-slate-100 text-slate-500 dark:bg-zink-600 hover:text-custom-500 hover:bg-custom-100"
                onClick={() => handleActionClick(uniqueId)}
              >
                <GripVertical className="size-4 m-auto" />
              </button>

              {showActionBar === uniqueId && (
                <div className="absolute z-[1000] right-0 mt-2 w-30 md:w-44 bg-white dark:bg-zink-600 dark:text-custom-100 shadow-md rounded-md p-2">
                  {/* Always show "Show Winners" */}
                  {rowData.soldTickets <= 0 && (
                    <>
                      <Link to={`/game-slots/edit/${uniqueId}`}>
                        <button
                          className="block w-full rounded-md text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-custom-100 dark:hover:bg-zink-600/80"
                        >
                          Edit Slot
                        </button>
                      </Link>
                      <button
                        onClick={() => {
                          setSelectedSlotId(uniqueId);
                          deleteToggle();
                        }}
                        className="block w-full text-left rounded-md px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-custom-100 dark:hover:bg-zink-600/80"
                      >
                        Delete Slot
                      </button>
                    </>
                  )}
                  <Link to={`details/${uniqueId}`}>
                    <button
                      onClick={() => showWinningNumbers(uniqueId)}
                      className="block w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-custom-100 hover:bg-slate-100 dark:hover:bg-zink-600/80 rounded-md"
                    >
                      Show Details
                    </button>
                  </Link>

                  {isLottery && (
                    <button
                      onClick={async () => {
                        try {
                          await runLotteryDrawNow(uniqueId);
                          toast.success("Lottery draw triggered. Check outbox for results.");
                        } catch (e: any) {
                          toast.error(e?.message || "Failed to trigger lottery draw");
                        }
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-custom-100 hover:bg-slate-100 dark:hover:bg-zink-600/80 rounded-md"
                    >
                      Run Draw Now
                    </button>
                  )}

                  {isLottery && (
                    <button
                      onClick={() => showWinningNumbers(uniqueId)}
                      className="block w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-custom-100 hover:bg-slate-100 dark:hover:bg-zink-600/80 rounded-md"
                    >
                      Show Winning Numbers
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [showActionBar]
  );

  return (
    <React.Fragment>
      <BreadCrumb title="Game Slots" pageTitle="Dashboard" />
      {isLoading && <Loader />}
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
      <ToastContainer closeButton={false} limit={1} />
      <div className="card" id="employeeTable">
        <div className="card-body w-full  relative overflow-x-scroll">
          <div className="flex items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              Game Slots (<b>{totalElements}</b>)
            </h6>
            <div className="shrink-0">
              <Link
                to="#!"
                data-modal-target="addGameSlotModal"
                type="button"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 add-employee"
                onClick={toggle}
              >
                <Plus className="inline-block size-4" />{" "}
                <span className="align-middle">Add Slot</span>
              </Link>
            </div>
          </div>
          {gameSlots && gameSlots.length > 0 ? (
            <div className="mt-10">
              <div className="">
                <TableTwoContainer
                  columns={columns || []}
                  data={gameSlots || []}
                />
              </div>
              <div className="mt-4 flex justify-end items-center gap-4">
                {/* Previous Page Button */}
                <button
                  onClick={goToPreviousPage}
                  disabled={!hasPreviousPage}
                  className="flex items-center px-3 py-1 bg-gray-500 text-white rounded disabled:opacity-50"
                >
                  <FaChevronLeft className="mr-2" />
                  Prev
                </button>

                {/* Page Indicator */}
                <span className="text-base font-medium">
                  Page {currentPage + 1} of {totalPages}
                </span>

                {/* Next Page Button */}
                <button
                  onClick={goToNextPage}
                  disabled={!hasNextPage || isFetching}
                  className="flex items-center px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
                >
                  {isFetching ? "Loading..." : "Next"}
                  <FaChevronRight className="ml-2" />
                </button>
              </div>
            </div>
          ) : (
            <div className="noresult">
              <div className="py-6 text-center">
                <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                <p className="mb-0 text-slate-500">
                  {isLoading
                    ? "Loading game slots..."
                    : "No game slots have been created yet."}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card mt-6" id="recurringSchedulesTable">
        <div className="card-body">
          <div className="flex items-center justify-between mb-4">
            <h6 className="text-15 grow flex items-center gap-2">
              <Repeat className="size-4" />
              Recurring schedules (slots created automatically)
            </h6>
            <button
              type="button"
              className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
              onClick={() => openScheduleModal()}
            >
              <Plus className="inline-block size-4 mr-1" />
              Add schedule
            </button>
          </div>
          <p className="text-sm text-slate-500 dark:text-zink-200 mb-4">
            Set daily or weekly schedules; slots will be created automatically so you don&apos;t have to add them manually.
          </p>
          {schedulesLoading ? (
            <p className="text-slate-500">Loading schedules...</p>
          ) : recurringSchedules.length === 0 ? (
            <p className="text-slate-500">No recurring schedules. Add one to auto-create slots.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-200 dark:border-zink-500">
                <thead className="bg-slate-100 dark:bg-zink-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Game</th>
                    <th className="px-3 py-2 text-left">Frequency</th>
                    <th className="px-3 py-2 text-left">Draw time</th>
                    <th className="px-3 py-2 text-left">Tickets</th>
                    <th className="px-3 py-2 text-left">Amount</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recurringSchedules.map((s: any) => (
                    <tr key={s.id} className="border-t border-slate-200 dark:border-zink-500">
                      <td className="px-3 py-2">{s.gameTitle || `Game #${s.gameId}`}</td>
                      <td className="px-3 py-2">{s.frequency === "DAILY" ? "Daily" : `Weekly (${["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][s.dayOfWeek] || s.dayOfWeek})`}</td>
                      <td className="px-3 py-2">{String(s.drawTime || "").substring(0, 5)}</td>
                      <td className="px-3 py-2">{s.totalNumberOfTickets}</td>
                      <td className="px-3 py-2">₦{Number(s.amountToBeWon || 0).toLocaleString()}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 text-xs rounded ${s.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                          {s.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-3 py-2 flex gap-1">
                        <button type="button" className="text-custom-500 hover:underline" onClick={() => openScheduleModal(s)}>
                          <Pencil className="size-4 inline" />
                        </button>
                        <button
                          type="button"
                          className="text-red-500 hover:underline"
                          onClick={() => {
                            if (window.confirm("Delete this schedule?")) scheduleDeleteMutation.mutate(s.id);
                          }}
                        >
                          <Trash2 className="size-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={showScheduleModal}
        onHide={() => {
          setShowScheduleModal(false);
          setScheduleEdit(null);
        }}
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[28rem] bg-white shadow rounded-md dark:bg-zink-600 max-h-[90vh] overflow-hidden flex flex-col"
      >
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-500">
          <Modal.Title className="text-16">{scheduleEdit ? "Edit schedule" : "Add recurring schedule"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 overflow-y-auto">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="inline-block mb-2 text-base font-medium">Select Game</label>
              <select
                className="form-input border-slate-200 dark:border-zink-500 w-full"
                value={scheduleForm.gameId}
                onChange={(e) => setScheduleForm({ ...scheduleForm, gameId: e.target.value })}
                disabled={!!scheduleEdit}
              >
                <option value="">Select a game</option>
                {gamesArray?.map((g: any) => (
                  <option key={g.id} value={g.id}>{g.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="inline-block mb-2 text-base font-medium">Repeat</label>
              <select
                className="form-input border-slate-200 dark:border-zink-500 w-full"
                value={scheduleForm.frequency}
                onChange={(e) => setScheduleForm({ ...scheduleForm, frequency: e.target.value })}
              >
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
              </select>
            </div>
            {scheduleForm.frequency === "WEEKLY" && (
              <div>
                <label className="inline-block mb-2 text-base font-medium">Day of week</label>
                <select
                  className="form-input border-slate-200 dark:border-zink-500 w-full"
                  value={scheduleForm.dayOfWeek}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, dayOfWeek: Number(e.target.value) })}
                >
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                  <option value={7}>Sunday</option>
                </select>
              </div>
            )}
            <div>
              <label className="inline-block mb-2 text-base font-medium">Draw time</label>
              <input
                type="time"
                className="form-input border-slate-200 dark:border-zink-500 w-full"
                value={scheduleForm.drawTime}
                onChange={(e) => setScheduleForm({ ...scheduleForm, drawTime: e.target.value })}
              />
            </div>
            <div>
              <label className="inline-block mb-2 text-base font-medium">Total number of tickets</label>
              <input
                type="number"
                className="form-input border-slate-200 dark:border-zink-500 w-full"
                value={scheduleForm.totalNumberOfTickets}
                onChange={(e) => setScheduleForm({ ...scheduleForm, totalNumberOfTickets: e.target.value })}
              />
            </div>
            {gamesArray?.find((g: any) => String(g?.id) === String(scheduleForm.gameId ?? ""))?.type === "RADIO" && (
              <>
                <div>
                  <label className="inline-block mb-2 text-base font-medium">Number of winners</label>
                  <input
                    type="number"
                    max={5}
                    className="form-input border-slate-200 dark:border-zink-500 w-full"
                    value={scheduleForm.numberOfWinners}
                    onChange={(e) => {
                      const num = e.target.value;
                      const n = Number(num) || 0;
                      const current = scheduleForm.winnerAmounts || [];
                      const next = n > 0 ? Array.from({ length: n }, (_, i) => (current[i] !== undefined && current[i] !== "" ? current[i] : "")) : [];
                      setScheduleForm({ ...scheduleForm, numberOfWinners: num, winnerAmounts: next });
                    }}
                  />
                </div>
                {Number(scheduleForm.numberOfWinners) > 0 && (
                  <div>
                    <label className="inline-block mb-2 text-base font-medium">Winner amount per position (₦)</label>
                    <p className="text-sm text-slate-500 dark:text-zink-400 mb-2">
                      Total amount to be won is the sum of all winner amounts below.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {Array.from({ length: Number(scheduleForm.numberOfWinners) }, (_, i) => (
                        <div key={i}>
                          <label className="inline-block mb-1 text-sm">Winner {i + 1}</label>
                          <input
                            type="number"
                            min={0}
                            className="form-input border-slate-200 dark:border-zink-500 w-full"
                            placeholder="e.g. 10000"
                            value={(scheduleForm.winnerAmounts || [])[i] ?? ""}
                            onChange={(e) => {
                              const arr = [...(scheduleForm.winnerAmounts || [])];
                              arr[i] = e.target.value;
                              setScheduleForm({ ...scheduleForm, winnerAmounts: arr });
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    {(() => {
                      const amounts = scheduleForm.winnerAmounts || [];
                      const sum = amounts.reduce((s: number, v: any) => s + (Number(v) || 0), 0);
                      return sum > 0 ? (
                        <p className="mt-2 text-sm text-slate-600 dark:text-zink-300">
                          Total amount to be won: <strong>{sum.toLocaleString()}</strong> ₦
                        </p>
                      ) : null;
                    })()}
                  </div>
                )}
                {(!scheduleForm.numberOfWinners || Number(scheduleForm.numberOfWinners) <= 0) && (
                  <div>
                    <label className="inline-block mb-2 text-base font-medium">Winner amount (₦)</label>
                    <input
                      type="number"
                      className="form-input border-slate-200 dark:border-zink-500 w-full"
                      value={scheduleForm.winnerAmount}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, winnerAmount: e.target.value })}
                    />
                  </div>
                )}
              </>
            )}
            <div>
              <label className="inline-block mb-2 text-base font-medium">Amount to be won (₦)</label>
              <input
                type="number"
                className="form-input border-slate-200 dark:border-zink-500 w-full"
                value={scheduleForm.amountToBeWon}
                onChange={(e) => setScheduleForm({ ...scheduleForm, amountToBeWon: e.target.value })}
                placeholder="e.g. 50000 (or sum of winner amounts above)"
              />
            </div>
            <div>
              <label className="inline-block mb-2 text-base font-medium">Win percentage for users (%)</label>
              <input
                type="number"
                max={50}
                className="form-input border-slate-200 dark:border-zink-500 w-full"
                value={scheduleForm.winnerPercentage}
                onChange={(e) => setScheduleForm({ ...scheduleForm, winnerPercentage: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="scheduleActive"
                checked={scheduleForm.active !== false}
                onChange={(e) => setScheduleForm({ ...scheduleForm, active: e.target.checked })}
              />
              <label htmlFor="scheduleActive">Active (create slots automatically)</label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="btn border-slate-200 dark:border-zink-500" onClick={() => setShowScheduleModal(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
              onClick={submitSchedule}
              disabled={scheduleCreateMutation.isPending || scheduleUpdateMutation.isPending}
            >
              {scheduleEdit ? (scheduleUpdateMutation.isPending ? "Updating..." : "Update") : (scheduleCreateMutation.isPending ? "Creating..." : "Add schedule")}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={show}
        onHide={toggle}
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">
            {!!isEdit ? "Edit Slot" : "Add Slot"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form
            className="create-form"
            id="create-form"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <input type="hidden" value="" name="id" id="id" />
            <input type="hidden" value="add" name="action" id="action" />
            <input type="hidden" id="id-field" />
            <div
              id="alert-error-msg"
              className="hidden px-4 py-3 text-sm text-red-500 border border-transparent rounded-md bg-red-50 dark:bg-red-500/20"
            ></div>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
              <div className="xl:col-span-12">
                <label
                  htmlFor="gameTypeSelect"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Select Game
                </label>
                <select
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  data-choices
                  data-choices-search-false
                  id="gameTypeSelect"
                  name="gameId"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.gameId}
                  disabled={isEdit}
                >
                  <option value="">Select a game</option>
                  {gamesArray?.map((game: any) => (
                    <option key={game.id} value={game.id}>
                      {game.title}
                    </option>
                  ))}
                </select>
                {validation.touched.gameId && validation.errors.gameId ? (
                  <p className="text-red-400">{validation.errors.gameId}</p>
                ) : null}
              </div>

              <div className="xl:col-span-12">
                <label
                  htmlFor="gamePriceInput"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Total Number of Tickets
                </label>
                <input
                  type="number"
                  id="gameQuantity"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="50"
                  name="totalNumberOfTickets"
                  onChange={validation.handleChange}
                  value={validation.values.totalNumberOfTickets || ""}
                />
                {validation.touched.totalNumberOfTickets &&
                validation.errors.totalNumberOfTickets ? (
                  <p className="text-red-400">
                    {validation.errors.totalNumberOfTickets}
                  </p>
                ) : null}
              </div>

              {isRadioGame && (
                <div className="xl:col-span-12">
                  <label
                    htmlFor="numberOfWinners"
                    className="inline-block mb-2 text-base font-medium"
                  >
                    Number of Winners for Radio Game
                  </label>
                  <input
                    type="number"
                    id="numberOfWinners"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="e.g. 2 or 3"
                    name="numberOfWinners"
                    min={1}
                    max={5}
                    onChange={validation.handleChange}
                    value={validation.values.numberOfWinners || ""}
                  />
                  {validation.touched.numberOfWinners &&
                  validation.errors.numberOfWinners ? (
                    <p className="text-red-400">
                      {validation.errors.numberOfWinners}
                    </p>
                  ) : null}
                </div>
              )}

              <div className="xl:col-span-12">
                <label
                  htmlFor="amountToBeWon"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Amount To Be Won(Incentive)
                </label>
                <input
                  type="number"
                  id="amountToBeWon"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Up to 50000 (or sum of winner amounts below)"
                  name="amountToBeWon"
                  onChange={validation.handleChange}
                  value={validation.values.amountToBeWon || ""}
                />
                {validation.touched.amountToBeWon &&
                validation.errors.amountToBeWon ? (
                  <p className="text-red-400">
                    {validation.errors.amountToBeWon}
                  </p>
                ) : null}
              </div>

              {isRadioGame && (
                <div className="xl:col-span-6">
                  <label className="inline-block mb-2 text-base font-medium">
                    Select Reward Type
                  </label>
                  <div className="flex gap-4 mb-4">
                    <label>
                      <input
                        type="radio"
                        name="rewardType"
                        value="percentage"
                        checked={radioOption === "percentage"}
                        onChange={() => setRadioOption("percentage")}
                        className="mr-1"
                      />
                      Win Percentage
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="rewardType"
                        value="amount"
                        checked={radioOption === "amount"}
                        onChange={() => setRadioOption("amount")}
                        className="mr-1"
                      />
                      Winner Amount
                    </label>
                  </div>
                </div>
              )}

              {/* Show winPercentage only if not RADIO, or if RADIO and percentage selected */}
              {(!isRadioGame ||
                (isRadioGame && radioOption === "percentage")) && (
                <div className="xl:col-span-6">
                  <label
                    htmlFor="winPercentage"
                    className="inline-block mb-2 text-base font-medium"
                  >
                    Enter Win Percentage for Users (%)
                  </label>
                  <input
                    type="number"
                    id="winPercentage"
                    name="winPercentage"
                    max="50"
                    placeholder="15"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    onChange={validation.handleChange}
                    value={validation.values.winPercentage || ""}
                  />
                  {validation.touched.winPercentage &&
                    validation.errors.winPercentage && (
                      <p className="text-red-400">
                        {validation.errors.winPercentage}
                      </p>
                    )}
                </div>
              )}

              {/* Show Winner 1, Winner 2, ... inputs when RADIO and Number of Winners is set (e.g. 2 → two inputs) */}
              {isRadioGame && numWinners > 0 && (
                <div className="xl:col-span-12">
                  <label className="inline-block mb-2 text-base font-medium">
                    Enter Winner Amount (per position)
                  </label>
                  <p className="text-sm text-slate-500 dark:text-zink-400 mb-2">
                    Amount to be won is the total of all winner amounts below.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: numWinners }, (_, i) => (
                      <div key={i}>
                        <label
                          htmlFor={`winnerAmount_${i}`}
                          className="inline-block mb-1 text-sm font-medium"
                        >
                          Winner {i + 1} amount
                        </label>
                        <input
                          type="number"
                          id={`winnerAmount_${i}`}
                          name={`winnerAmounts[${i}]`}
                          placeholder="e.g. 10000"
                          min={0}
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 w-full"
                          value={(validation.values.winnerAmounts as any[])?.[i] ?? ""}
                          onChange={(e) => {
                            const arr = [...((validation.values.winnerAmounts as any[]) || [])];
                            arr[i] = e.target.value;
                            validation.setFieldValue("winnerAmounts", arr);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  {(() => {
                    const amounts = (validation.values.winnerAmounts as any[]) || [];
                    const sum = amounts.reduce((s: number, v: any) => s + (Number(v) || 0), 0);
                    return sum > 0 ? (
                      <p className="mt-2 text-sm text-slate-600 dark:text-zink-300">
                        Total amount to be won: <strong>{sum.toLocaleString()}</strong>
                      </p>
                    ) : null;
                  })()}
                </div>
              )}
              {/* Single winner amount when RADIO + amount selected but no number of winners entered yet */}
              {isRadioGame && radioOption === "amount" && numWinners <= 0 && (
                <div className="xl:col-span-6">
                  <label
                    htmlFor="winnerAmount"
                    className="inline-block mb-2 text-base font-medium"
                  >
                    Enter Winner Amount
                  </label>
                  <input
                    type="number"
                    id="winnerAmount"
                    name="winnerAmount"
                    placeholder="1000"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    onChange={validation.handleChange}
                    value={validation.values.winnerAmount || ""}
                  />
                  {validation.touched.winnerAmount &&
                    validation.errors.winnerAmount && (
                      <p className="text-red-400">
                        {validation.errors.winnerAmount}
                      </p>
                    )}
                </div>
              )}

              {isTriviaGame && (
                <>
                  <div className="xl:col-span-12">
                    <label className="inline-block mb-2 text-base font-medium">
                      Question Selection
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="questionSelectionType"
                          value="auto"
                          checked={
                            validation.values.questionSelectionType === "auto"
                          }
                          onChange={(e) =>
                            validation.setFieldValue(
                              "questionSelectionType",
                              e.target.value
                            )
                          }
                        />
                        Auto Generate Questions
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="questionSelectionType"
                          value="select"
                          checked={
                            validation.values.questionSelectionType === "select"
                          }
                          onChange={(e) =>
                            validation.setFieldValue(
                              "questionSelectionType",
                              e.target.value
                            )
                          }
                        />
                        Select a Question Set
                      </label>
                    </div>
                  </div>

                  {validation.values.questionSelectionType === "select" && (
                    <div className="xl:col-span-12">
                      <label className="inline-block mb-2 text-base font-medium">
                        Select Question Set
                      </label>
                      <select
                        className="form-input border-slate-200 dark:border-zink-500"
                        name="questionSetId"
                        value={validation.values.questionSetId}
                        onChange={validation.handleChange}
                      >
                        <option value="">Select a Question Set</option>
                        {questionSets.map((set) => (
                          <option key={set.id} value={set.id}>
                            {set.name}
                          </option>
                        ))}
                      </select>
                      {validation.touched.questionSetId &&
                        validation.errors.questionSetId && (
                          <p className="text-red-400">
                            {validation.errors.questionSetId}
                          </p>
                        )}
                    </div>
                  )}
                </>
              )}

              <div className="xl:col-span-12">
                <label
                  htmlFor="gameDrawStartInput"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Draw Date & Time
                </label>
                <Flatpickr
                  date-enable-time
                  id="joiningDateInput"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  options={{
                    enableTime: true,
                    time_24hr: false,
                    dateFormat: "Y-m-d H:i:S",
                    altInput: true,
                    altFormat: "F j, Y at h:i K",
                    static: true,
                  }}
                  placeholder="Select Date"
                  name="drawDate"
                  onChange={(date) => {
                    const localDate = new Date(
                      date[0].getTime() - date[0].getTimezoneOffset() * 60000
                    );
                    validation.setFieldValue(
                      "drawDate",
                      localDate.toISOString()
                    );
                  }}
                  value={validation.values.drawDate || ""}
                />
                {validation.touched.drawDate && validation.errors.drawDate ? (
                  <p className="text-red-400">{validation.errors.drawDate}</p>
                ) : null}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="reset"
                id="close-modal"
                data-modal-close="addGameSlotModal"
                className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-600 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                onClick={toggle}
              >
                Cancel
              </button>
              <button
                type="submit"
                id="addNew"
                disabled={isCreating}
                onClick={validation.handleSubmit}
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                {!!isEdit ? "Update" : "Add Slot"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal
        show={winningNumbersModal}
        onHide={() => {
          setWinningNumbersModal(false);
          setSelectedGameSlotForWinningNumbers(null);
        }}
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-500">
          <Modal.Title className="text-16">Winning Numbers</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {selectedGameSlotForWinningNumbers && (
            <WinningNumbersContent game={selectedGameSlotForWinningNumbers} />
          )}
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default GameSlots;
